import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { thoughtMeta, type Thought } from "../data/thoughts";
import { BODY, PANEL_HEADING } from "../utils/typography";
type ThoughtFeatureProps = {
  thought: Thought;
  showSeeAll?: boolean;
};
const ease = [0.23, 1, 0.32, 1] as const;
export function ThoughtFeature({
  thought,
  showSeeAll = true
}: ThoughtFeatureProps) {
  const reduce = useReducedMotion();
  return <motion.article initial={reduce ? false : {
    opacity: 0,
    y: 24,
    scale: 0.98
  }} whileInView={{
    opacity: 1,
    y: 0,
    scale: 1
  }} viewport={{
    once: true,
    amount: 0.25
  }} transition={{
    duration: 0.3,
    ease,
    delay: 0.2
  }} className="mt-24 grid grid-cols-1 gap-10 rounded-3xl bg-espresso px-8 py-10 md:mt-32 md:px-14 lg:mt-40 md:py-16 lg:grid-cols-12 lg:gap-12 lg:px-20 lg:py-24" data-id="element-452" data-mp="26o4k9ejpfa2a">
      <div className="flex flex-col justify-between gap-4 lg:col-span-4" data-id="element-453" data-mp="3rz4niso0locy">
        <p className="text-sm font-medium uppercase tracking-wide text-mustard" data-id="element-454" data-mp="1yae1y1e514jy">
          {thought.category}
        </p>
        <p className="text-sm text-cream/50" data-id="element-455" data-mp="1uabx5jd3vv0g">{thoughtMeta(thought)}</p>
      </div>

      <div className="lg:col-span-8" data-id="element-456" data-mp="1p9o6iysshg6r">
        <h2 className={`${PANEL_HEADING} text-cream`} data-id="element-457" data-mp="1pctweg98sm3j">
          {thought.title}
        </h2>
        <p className={`mt-8 max-w-2xl ${BODY} text-cream/70`} data-id="element-458" data-mp="1kdqu33knctz9">
          {thought.excerpt}
        </p>

        <div className="mt-12 flex flex-wrap gap-3" data-id="element-459" data-mp="1fenvo1z69m09">
          {thought.status ? <span className="flex h-12 items-center whitespace-nowrap rounded-full bg-rust px-6 text-sm font-medium uppercase text-cream" data-id="element-460" data-mp="1akjrtrl7du3e">
              {thought.status}
            </span> : <Link to={`/thoughts/${thought.slug}`} className="flex h-12 items-center gap-3 whitespace-nowrap rounded-full bg-rust px-6 text-sm font-medium uppercase text-cream transition-colors duration-150 ease-out hover:bg-mustard hover:text-espresso" data-id="element-461" data-mp="11j21lge30c0z">
              Read the thought
              <ArrowRightIcon className="h-4 w-4" data-id="element-462" data-mp="my6c93tnj17c" />
            </Link>}
          {showSeeAll && <Link to="/thoughts" className="flex h-12 items-center whitespace-nowrap rounded-full px-6 text-sm font-medium uppercase text-cream ring-1 ring-cream/20 transition-colors duration-150 ease-out hover:bg-cream hover:text-espresso" data-id="element-463" data-mp="8z6nzxa1wobp">
              See all thoughts
            </Link>}
        </div>
      </div>
    </motion.article>;
}